class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.initializeProperties();
    this.initializeElements();
    this.attachEventListeners();
  }

  // === INITIALIZATION METHODS ===
  
  initializeProperties() {
    this.loading = false;
    this.currentProducts = [];
    this.currentCollections = [];
    this.searchTimeout = null;
  }

  initializeElements() {
    this.searchInput = this.querySelector('#src-search-suggest');
    this.searchResults = this.querySelector('#searchResults');
    this.searchButton = this.querySelector('#src-search-button');
    this.productsColumn = this.querySelector('.productsColumn');
    this.collectionsColumn = this.querySelector('.collectionsColumn');
    this.viewAllLink = this.querySelector('#search-all-results');
  }

  attachEventListeners() {
    // Search input events
    this.searchInput.addEventListener('input', this.debounce(this.handleInput.bind(this), 300));
    this.searchInput.addEventListener('keypress', this.handleKeyPress.bind(this));
    this.searchInput.addEventListener('focusin', this.handleFocusIn.bind(this));
    
    // Dynamic width adjustment
    this.searchInput.addEventListener('input', () => {
      this.searchResults.style.width = this.getBoundingClientRect().width + 'px';
    });
    
    // Search button
    this.searchButton.addEventListener('click', this.searchButtonClick.bind(this));
    
    // Outside click to close
    document.addEventListener('click', this.handleOutsideClick.bind(this));
    
    // View all results link
    if (this.viewAllLink) {
      this.viewAllLink.addEventListener('click', this.handleViewAllClick.bind(this));
    }
  }

  // === SEARCH LOGIC METHODS ===

  async handleInput() {
    const searchTerm = this.searchInput.value.trim();
    
    if (searchTerm.length === 0) {
      this.clearResults();
      return;
    }

    this.setLoadingState(true);
    this.showResults();

    try {
      await this.performSearch(searchTerm);
    } catch (error) {
      console.error('Search error:', error);
    }

    this.displayResults();
    this.setLoadingState(false);
  }

  async performSearch(searchTerm) {
    const results = await this.enhancedSearch(searchTerm);
    this.currentProducts = results.products || [];
    this.currentCollections = results.collections || [];
  }

  // === SKU DETECTION AND SEARCH METHODS ===

  isLikelySku(query) {
    return query.length >= 3 && 
           !query.includes(' ') && 
           /\d/.test(query) &&
           /^[a-zA-Z0-9\-_]+$/.test(query);
  }

  async enhancedSearch(query) {
    const normalizedQuery = query.toLowerCase();
    const isLikelySku = this.isLikelySku(normalizedQuery);
    
    // Use general search that includes SKU field for partial matching
    const searchURL = `/search/suggest.json?q=${normalizedQuery}&resources[limit_scope]=each&resources[limit]=5&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor,tag,body`;
    
    try {
      const response = await fetch(searchURL);
      const data = await response.json();
      const products = data.resources.results.products || [];
      const collections = data.resources.results.collections || [];
      
      const scoredProducts = this.processAndScoreResults(products, normalizedQuery, isLikelySku);
      
      return {
        products: scoredProducts,
        collections: collections.slice(0, 3) // Limit collections to top 3
      };
    } catch (error) {
      console.error('Enhanced search error:', error);
      return { products: [], collections: [] };
    }
  }


  processAndScoreResults(products, query, isSkuSearch = false) {
    try {
      let scoredProducts = products
        .map(product => this.scoreProduct(product, query, isSkuSearch))
        .filter(product => product.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 8);
      
      // Fallback: If no products scored > 0, return all products with default scores
      if (scoredProducts.length === 0 && products.length > 0) {
        console.warn('No products scored > 0, using fallback display');
        scoredProducts = products
          .map(product => ({
            ...product,
            score: this.calculateFallbackScore(product, query)
          }))
          .sort((a, b) => b.score - a.score)
          .slice(0, 8);
      }
      
      return scoredProducts;
    } catch (error) {
      console.error('Error in processAndScoreResults, using fallback:', error);
      // Emergency fallback: return original products with default order
      return products
        .map(product => ({ ...product, score: 1 }))
        .slice(0, 8);
    }
  }

  scoreProduct(product, query, isSkuSearch = false) {
    const queryLower = query.toLowerCase();
    let score = 0;
    
    // For SKU searches, if the product was returned by the SKU-specific API,
    // it should get a high score even if variants array is empty
    if (isSkuSearch) {
      // Check if product has variants and try SKU scoring
      if (product.variants && product.variants.length > 0) {
        score += this.calculateSkuScore(product.variants, queryLower);
      } else {
        // Product was returned by SKU search but variants array is empty
        // This happens when Shopify's SKU search finds the product but doesn't include variants
        score += 25; // High score for SKU search results
      }
    }
    
    // Title scoring (always check, even for SKU searches as backup)
    score += this.calculateTitleScore(product.title, queryLower, isSkuSearch);
    
    // Other field scoring
    score += this.calculateFieldScore(product, queryLower, isSkuSearch);
    
    return { ...product, score };
  }

  calculateSkuScore(variants, query) {
    for (const variant of variants) {
      if (variant.sku) {
        if (variant.sku.toLowerCase() === query) {
          return 30; // Exact SKU match
        }
        if (variant.sku.toLowerCase().includes(query)) {
          return 15; // Partial SKU match
        }
      }
    }
    return 0;
  }

  calculateTitleScore(title, query, isSkuSearch = false) {
    if (!title) return 0;
    
    const titleLower = title.toLowerCase();
    const multiplier = isSkuSearch ? 0.5 : 1; // Lower priority for title matches in SKU searches
    const queryWords = query.toLowerCase().split(' ').filter(word => word.length > 0);
    
    if (titleLower === query) {
      return 20 * multiplier; // Exact title match
    }
    if (titleLower.includes(query)) {
      return 10 * multiplier; // Partial title match
    }
    
    // Check for individual word matches for multi-word queries
    let wordMatchScore = 0;
    let matchedWords = 0;
    
    for (const word of queryWords) {
      if (titleLower.includes(word)) {
        matchedWords++;
        wordMatchScore += 3; // 3 points per matched word
      }
    }
    
    // Bonus for matching multiple words
    if (matchedWords >= 2) {
      wordMatchScore += 5; // Additional bonus for 2+ words
    }
    if (matchedWords >= 3) {
      wordMatchScore += 10; // Additional bonus for 3+ words
    }
    
    return wordMatchScore * multiplier;
  }

  calculateFieldScore(product, query, isSkuSearch) {
    let score = 0;
    const queryWords = query.toLowerCase().split(' ').filter(word => word.length > 0);
    const multiplier = isSkuSearch ? 0.3 : 1; // Lower priority for field matches in SKU searches
    
    // Check vendor for each word
    if (product.vendor) {
      const vendorLower = product.vendor.toLowerCase();
      for (const word of queryWords) {
        if (vendorLower.includes(word)) {
          score += 2 * multiplier;
        }
      }
    }
    
    // Check product type for each word
    if (product.product_type) {
      const typeLower = product.product_type.toLowerCase();
      for (const word of queryWords) {
        if (typeLower.includes(word)) {
          score += 1.5 * multiplier;
        }
      }
    }
    
    // Check tags for each word
    if (product.tags && Array.isArray(product.tags)) {
      for (const tag of product.tags) {
        const tagLower = tag.toLowerCase();
        for (const word of queryWords) {
          if (tagLower.includes(word)) {
            score += 1 * multiplier;
            break; // Only count each tag once
          }
        }
      }
    }
    
    // Check body/description for each word (but with lower weight)
    if (product.body) {
      const bodyLower = product.body.toLowerCase();
      for (const word of queryWords) {
        if (bodyLower.includes(word)) {
          score += 0.5 * multiplier;
        }
      }
    }
    
    return score;
  }

  calculateFallbackScore(product, query) {
    // Simple fallback scoring that's more lenient
    const queryLower = query.toLowerCase();
    const titleLower = (product.title || '').toLowerCase();
    let score = 1; // Base score for all products
    
    // Check for any word matches in title
    const queryWords = queryLower.split(' ').filter(word => word.length > 0);
    for (const word of queryWords) {
      if (titleLower.includes(word)) {
        score += 2;
      }
    }
    
    // Check if query appears anywhere in the product data
    const productString = JSON.stringify(product).toLowerCase();
    if (productString.includes(queryLower)) {
      score += 1;
    }
    
    return score;
  }

  // === DISPLAY METHODS ===

  displayResults() {
    this.clearDisplay();
    this.displayProducts();
    this.displayCollections();
  }

  clearDisplay() {
    if (this.productsColumn) this.productsColumn.innerHTML = '';
    if (this.collectionsColumn) this.collectionsColumn.innerHTML = '';
  }

  displayProducts() {
    if (!this.productsColumn || !this.currentProducts.length) return;
    
    this.currentProducts.forEach(product => {
      const productElement = this.createProductElement(product);
      this.productsColumn.appendChild(productElement);
    });
  }

  displayCollections() {
    if (!this.collectionsColumn || !this.currentCollections.length) return;
    
    this.currentCollections.forEach(collection => {
      const collectionItem = this.createCollectionElement(collection);
      this.collectionsColumn.appendChild(collectionItem);
    });
  }

  createCollectionElement(collection) {
    const item = document.createElement('div');
    item.classList.add('collectionItem');
    item.innerHTML = `<a href="${collection.url}">${collection.title}</a>`;
    return item;
  }

  createProductElement(product) {
    const item = document.createElement('div');
    const instock = product.available ? 'available' : 'unavailable';
    
    item.classList.add('item', instock);
    item.innerHTML = this.getProductHTML(product);
    
    item.addEventListener('click', () => {
      this.navigateToProduct(product.url);
    });
    
    return item;
  }

  getProductHTML(product) {
    const priceDisplay = this.formatPrice(product);
    const imageUrl = this.getProductImage(product);
    
    return `
      <div class="image-container">
        <img src="${imageUrl}" alt="${product.title}">
      </div>
      <div class="detail">
        <div class="title">${product.title}</div>
        <div class="price">${priceDisplay}</div>
      </div>
    `;
  }

  formatPrice(product) {
    const price = product.price || product.price_min || 0;
    const comparePrice = product.compare_at_price_max || product.compare_at_price;
    
    // Only show compare price if it's higher than the current price
    if (comparePrice && comparePrice > price) {
      return `<span class="compare">${this.formatCurrency(comparePrice)}</span> ${this.formatCurrency(price)}`;
    }
    
    return this.formatCurrency(price);
  }

  formatCurrency(amount) {
    // Use the theme's built-in currency formatter
    if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(amount, theme.moneyFormat);
    }
    
    // Fallback to basic formatting if theme functions aren't available
    return `$${(amount / 100).toFixed(2)}`;
  }


  getProductImage(product) {
    return product.image || product.featured_image || 'https://via.placeholder.com/60x60';
  }

  // === EVENT HANDLERS ===

  handleKeyPress(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.handleSearchSubmission();
    }
  }

  handleFocusIn() {
    if (this.hasResults()) {
      this.showResults();
    }
  }

  handleOutsideClick(event) {
    if (!this.contains(event.target)) {
      this.hideResults();
    }
  }

  handleViewAllClick(event) {
    event.preventDefault();
    const searchTerm = this.searchInput.value.trim();
    this.navigateToSearchPage(searchTerm);
  }

  searchButtonClick(event) {
    event.preventDefault();
    this.handleSearchSubmission();
  }

  async handleSearchSubmission() {
    const searchTerm = this.searchInput.value.trim();
    if (searchTerm.length === 0) return;
    
    // Wait for any pending search to complete
    await this.waitForSearchCompletion();
    
    // Check if we have a direct SKU match for navigation
    const skuMatch = this.findSkuMatch(searchTerm);
    if (skuMatch) {
      this.navigateToProduct(skuMatch.url);
    } else {
      this.navigateToSearchPage(searchTerm);
    }
  }

  // === UTILITY METHODS ===

  debounce(func, delay) {
    return (...args) => {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => func.apply(this, args), delay);
    };
  }

  hasResults() {
    return this.currentProducts.length > 0 || this.currentCollections.length > 0;
  }

  findSkuMatch(searchTerm) {
    const normalizedTerm = searchTerm.toLowerCase();
    return this.currentProducts.find(product => {
      if (product.variants) {
        return product.variants.some(variant => 
          variant.sku && variant.sku.toLowerCase() === normalizedTerm
        );
      }
      return false;
    });
  }

  async waitForSearchCompletion() {
    return new Promise(resolve => {
      const checkInterval = setInterval(() => {
        if (!this.loading) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 50);
    });
  }

  setLoadingState(loading) {
    this.loading = loading;
  }

  showResults() {
    this.searchResults.classList.remove('hidden');
  }

  hideResults() {
    this.searchResults.classList.add('hidden');
  }

  clearResults() {
    this.currentProducts = [];
    this.currentCollections = [];
    this.hideResults();
    this.clearDisplay();
  }

  // === NAVIGATION METHODS ===

  navigateToProduct(url) {
    if (url) {
      window.location.href = url;
    }
  }

  navigateToCollection(url) {
    if (url) {
      window.location.href = url;
    }
  }


  navigateToSearchPage(searchTerm) {
    if (searchTerm) {
      window.location.href = `/search?q=${encodeURIComponent(searchTerm)}`;
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
